import { sendAudioInvitation } from '../../service/interpreter.service';
import { ACTION_TYPES } from '../../constants/store.constants';
import DialPadAction from './DialPadAction';
import setErrorAction from './errorActions';

const setInterpreterServiceArea = data => dispatch => {
  dispatch({ type: ACTION_TYPES.SET_INTERPRETER_SERVICE_AREA, payload: data });
  const inviteButtonStatus = !!data;
  dispatch({ type: ACTION_TYPES.ENABLE_INVITE_BUTTON, payload: inviteButtonStatus });
  if (data) {
    dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: null });
  }
};

/**
 * Will be triggered for Audio Interpreter(Service Area, Dialpad), Guest Audio Invitation (Dialpad)
 * @param {payload} required - backend request payload
 * @param {appConfig, forServiceArea, contact_number} appConfig - required for defaultViewTimer config,
 * forServiceArea - required to distinguish between service area vs dialpad, contact_number to reset dialpad default view
 */
const inviteAudioInterpreter =
  (payload, { appConfig, forServiceArea, contact_number }) =>
  async dispatch => {
    try {
      // initiate default view
      dispatch({ type: ACTION_TYPES.SET_MENU_ITEM_DISABLE, payload: false });
      if (forServiceArea) {
        dispatch(setServiceAreaDefaultView(appConfig));
      } else {
        dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: true, status: false, loading: true } });
        dispatch(DialPadAction.setDialPadDefaultView({ contact_number: contact_number, appConfig: appConfig }));
      }

      // trigger backend service call
      const response = await sendAudioInvitation(payload);
      if (response?.status) {
        dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: forServiceArea, status: true } });
      }
      if (response?.sessionID) {
        dispatch({ type: ACTION_TYPES.SET_TPLC_SESSIONID, payload: { sessionID: response.sessionID } });
      }
    } catch (err) {
      dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: false, status: false, loading: false } });
      dispatch(setErrorAction.setErrorHandler(err));
    }
  };

let resetServiceAreaTimer = null;
const clearInviteResetTimer = () => {
  if (resetServiceAreaTimer) {
    clearTimeout(resetServiceAreaTimer);
    resetServiceAreaTimer = null;
  }
};

const resetInviteStateOnContextSwitch = () => dispatch => {
  clearInviteResetTimer();
  dispatch({ type: ACTION_TYPES.SET_INVITE_BUTTON_COOLDOWN, payload: false });
  dispatch({ type: ACTION_TYPES.ENABLE_INVITE_BUTTON, payload: true });
  dispatch({ type: ACTION_TYPES.DISABLE_LANGUAGE_LIST, payload: false });
  dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: false });
};

const setServiceAreaDefaultView = appConfig => dispatch => {
  clearInviteResetTimer();
  dispatch({ type: ACTION_TYPES.ENABLE_INVITE_BUTTON, payload: false });
  dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: true });
  const {
    clientSettings: { defaultViewTimer = null }
  } = appConfig;
  if (defaultViewTimer) {
    resetServiceAreaTimer = setTimeout(() => {
      dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: false });
    }, defaultViewTimer);
  }
};

const setInviteButtonEnable =
  (appConfig, { disableLanguageListDuringCooldown = false, disableServiceAreaDuringCooldown = false } = {}) =>
  dispatch => {
    clearInviteResetTimer();
    dispatch({ type: ACTION_TYPES.SET_INVITE_BUTTON_COOLDOWN, payload: false });
    dispatch({ type: ACTION_TYPES.ENABLE_INVITE_BUTTON, payload: false });
    const defaultViewTimer = appConfig?.clientSettings?.defaultViewTimer ?? null;
    if (disableLanguageListDuringCooldown) {
      dispatch({ type: ACTION_TYPES.DISABLE_LANGUAGE_LIST, payload: true });
    }
    if (disableServiceAreaDuringCooldown) {
      dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: true });
    }
    if (defaultViewTimer) {
      dispatch({ type: ACTION_TYPES.SET_INVITE_BUTTON_COOLDOWN, payload: true });
      resetServiceAreaTimer = setTimeout(() => {
        dispatch({ type: ACTION_TYPES.SET_INVITE_BUTTON_COOLDOWN, payload: false });
        dispatch({ type: ACTION_TYPES.ENABLE_INVITE_BUTTON, payload: true });
        dispatch({ type: ACTION_TYPES.DISABLE_LANGUAGE_LIST, payload: false });
        dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: false });
        dispatch({ type: ACTION_TYPES.SET_INTERPRETER_SUPPRESS_AUTO_SELECT, payload: false });
      }, defaultViewTimer);
    } else if (disableLanguageListDuringCooldown || disableServiceAreaDuringCooldown) {
      dispatch({ type: ACTION_TYPES.DISABLE_LANGUAGE_LIST, payload: false });
      dispatch({ type: ACTION_TYPES.DISABLE_SERVICE_AREA, payload: false });
    }
  };

const disableServiceArea = data => ({
  type: ACTION_TYPES.DISABLE_SERVICE_AREA,
  payload: data
});

const prePhoneCallDispatch =
  ({ forServiceArea, contact_number, appConfig }) =>
  dispatch => {
    dispatch({ type: ACTION_TYPES.SET_MENU_ITEM_DISABLE, payload: false });
    if (forServiceArea) {
      dispatch(setServiceAreaDefaultView(appConfig));
    } else {
      dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: true, status: false, loading: true } });
      dispatch(DialPadAction.setDialPadDefaultView({ contact_number, appConfig }));
    }
  };

const successPhoneCallDispatch = (inviteAudioPayload, forServiceArea) => dispatch => {
  sendAudioInvitation(inviteAudioPayload);
  dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: forServiceArea, status: true } });
};

const successInterpreterCallDispatch = forServiceArea => dispatch => {
  dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: forServiceArea, status: true } });
};

const errorPhoneCallDispatch = () => dispatch => {
  dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: false, status: false, loading: false } });
  dispatch(setErrorAction.setErrorHandler({ code: 16000 }));
};

const errorInterpreterCallDispatch = () => dispatch => {
  dispatch({ type: ACTION_TYPES.INVITE_BUTTON_CLICKED, payload: { inviteButtonClicked: false, status: false, loading: false } });
  dispatch(setErrorAction.setErrorHandler({ code: 16000 }));
};

export default {
  setInterpreterServiceArea,
  inviteAudioInterpreter,
  setServiceAreaDefaultView,
  resetInviteStateOnContextSwitch,
  disableServiceArea,
  prePhoneCallDispatch,
  successPhoneCallDispatch,
  errorPhoneCallDispatch,
  successInterpreterCallDispatch,
  errorInterpreterCallDispatch,
  setInviteButtonEnable
};
